import React, { useCallback, useEffect, useRef, useState } from 'react';
import { multiFactor, onIdTokenChanged, reload, sendEmailVerification, signOut, type User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, isManagementPortal } from '../../firebaseConfig';
import { ACCOUNT_SECURITY_EVENT, MFA_RECOVERY_EVENT, MFA_LOGIN_NOTICE, mfaError } from '../common/mfa';
import { clearClientWorkspaceData } from '../common/clientWorkspaceData';
import { isOrganizationAccess } from '../common/organizationAccessMode';
import type { MfaAccessReason } from '../../functions/src/common/mfaPolicy';
import MfaEnrollment from './MfaEnrollment';
import MfaSecuritySettings from './MfaSecuritySettings';
import MfaRecoveryDialog from './MfaRecoveryDialog';
import { mfaStyles as styles } from './mfaStyles';

interface MfaStatus {
    reason: MfaAccessReason;
    requiredAfter: number | null;
    resetAfter: number;
    canRecover: boolean;
    loginNotice?: string;
}

export default function MfaGate({ children }: React.PropsWithChildren) {
    const [state, setState] = useState<{ user: User | null; status?: MfaStatus; error?: string; checking?: boolean }>({ user: null, checking: true });
    const [securityOpen, setSecurityOpen] = useState(false);
    const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const sequence = useRef(0);
    const userUid = state.user?.uid;
    const canRecover = isManagementPortal && state.status?.canRecover === true;
    const check = useCallback(async (user: User | null) => {
        const revision = ++sequence.current;
        if (!user) {
            setState({ user: null });
            setSecurityOpen(false);
            setRecoveryEmail(null);
            return;
        }
        // CAPTCHA focus changes must preserve an account's ongoing MFA setup.
        setState(previous => previous.user?.uid === user.uid && previous.status ? previous : { user, checking: true });
        try {
            const response = await httpsCallable<unknown, MfaStatus>(functions, 'getMfaStatus')({});
            if (revision !== sequence.current || auth.currentUser?.uid !== user.uid)
                return;
            if (response.data.reason === 'fresh-login') {
                clearClientWorkspaceData();
                sessionStorage.setItem(MFA_LOGIN_NOTICE, response.data.loginNotice || 'Account security changed. Sign in again to complete required MFA.');
                setState({ user, status: response.data });
                await signOut(auth);
                return;
            }
            setState({ user, status: response.data });
        } catch (error) {
            if (revision === sequence.current)
                setState({ user, error: mfaError(error) });
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onIdTokenChanged(auth, user => { void check(user); });
        return () => { ++sequence.current; unsubscribe(); };
    }, [check]);
    useEffect(() => {
        if (!userUid)
            return;
        const refresh = () => { if (auth.currentUser) void check(auth.currentUser); };
        const fail = (error: string) => { ++sequence.current; setState(previous => ({ ...previous, status: undefined, checking: false, error })); };
        const stops = [onSnapshot(doc(db, 'security_settings/mfa'), refresh, () => fail('Could not verify the MFA policy. Reload or contact support.')),
            onSnapshot(doc(db, 'mfa_security', userUid), refresh, () => fail('Could not verify account security. Reload or contact support.'))];
        window.addEventListener('focus', refresh);
        return () => { for (const stop of stops) stop(); window.removeEventListener('focus', refresh); };
    }, [userUid, check]);
    useEffect(() => {
        const open = () => { setRecoveryEmail(null); setSecurityOpen(true); };
        const recover = (event: Event) => {
            if (!canRecover)
                return;
            const email = (event as CustomEvent<{ email?: unknown }>).detail?.email;
            setSecurityOpen(false);
            setRecoveryEmail(typeof email === 'string' ? email : '');
        };
        window.addEventListener(ACCOUNT_SECURITY_EVENT, open);
        window.addEventListener(MFA_RECOVERY_EVENT, recover);
        return () => { window.removeEventListener(ACCOUNT_SECURITY_EVENT, open); window.removeEventListener(MFA_RECOVERY_EVENT, recover); };
    }, [canRecover]);

    const verifyEmail = async (send: boolean) => {
        setBusy(true);
        setNotice('');
        try {
            const user = auth.currentUser;
            if (!user)
                return;
            if (send) {
                await sendEmailVerification(user, { url: window.location.origin, handleCodeInApp: false });
                setNotice('Verification email sent. Open its link, then choose “I verified my email”.');
            } else {
                await reload(user);
                await user.getIdToken(true);
                await check(user);
                if (!user.emailVerified)
                    setNotice('Your email is not verified yet. Open the latest verification link.');
            }
        } catch (error) {
            setNotice(mfaError(error));
        } finally {
            setBusy(false);
        }
    };

    if (state.checking)
        return <main className={styles.page}><p role="status" className={styles.text}>Loading…</p></main>;
    if (!state.user)
        return <React.Fragment key="signed-out">{children}</React.Fragment>;
    if (state.status?.reason === 'ready')
        return <React.Fragment key={state.user?.uid}>{children}{securityOpen && !isOrganizationAccess &&
            <MfaSecuritySettings canRecover={canRecover} onClose={() => setSecurityOpen(false)} />}
            {recoveryEmail !== null && canRecover && !isOrganizationAccess && <MfaRecoveryDialog key={recoveryEmail} email={recoveryEmail} onClose={() => setRecoveryEmail(null)} />}</React.Fragment>;

    const reason = state.status?.reason;
    const enrolled = state.user ? multiFactor(state.user).enrolledFactors.length > 0 : false;
    return <main className={styles.page}><section className={styles.panel}>
        <h1 className="text-2xl font-bold">{reason === 'email' ? 'Verify your email' : reason === 'enrollment' && !enrolled ? 'Secure your account' : 'Account security'}</h1>
        {state.error && <p role="alert" className={styles.error}>{state.error}</p>}
        {notice && <p role="status" className={styles.notice}>{notice}</p>}
        {reason === 'email' && <><p className={styles.text}>Verify {state.user?.email} before setting up required MFA.</p><div className="flex flex-wrap gap-3"><button disabled={busy} className={styles.button} onClick={() => void verifyEmail(true)}>Send verification email</button><button disabled={busy} className={styles.secondary} onClick={() => void verifyEmail(false)}>I verified my email</button></div></>}
        {reason === 'enrollment' && !enrolled && <><p className={styles.text}>Everyone must verify a security method before account access. Use a text message or an authenticator app.</p><MfaEnrollment onComplete={() => {
            sessionStorage.setItem(MFA_LOGIN_NOTICE, 'MFA setup complete. Sign in again to verify your new security method.');
            void signOut(auth);
        }} /></>}
        {reason === 'enrollment' && enrolled && <p className={styles.text}>Sign out and sign in again with one of your enrolled methods to finish verification.</p>}
        {reason === 'configuration' && <p role="alert" className={styles.text}>MFA setup is not ready for this site. Contact support before continuing.</p>}
        {reason === 'recovery-pending' && <p role="alert" className={styles.text}>Account recovery is still in progress. Contact support to finish your identity check.</p>}
        {reason === 'fresh-login' && <p role="status" className={styles.text}>Ending your old session. Please sign in again.</p>}
        {state.user && <button className={styles.secondary} onClick={() => { clearClientWorkspaceData(); void signOut(auth); }}>Sign out</button>}
        {(state.error || reason === 'configuration') && <button className={styles.secondary} onClick={() => void check(auth.currentUser)}>Retry</button>}
    </section></main>;
}
